import { Fragment } from 'react';
import { Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { EmulatorType } from '@localstack/types';
import { EMULATOR_LABELS } from '@localstack/constants';

import { EmulatorIcon } from '../EmulatorIcon';

const AWSPillWrapper = styled('div')(({ theme }) => ({
  borderRadius: '4px',
  background: theme.palette.aws.background,
  color: theme.palette.aws.text,
  width: 'fit-content',
  display: 'flex',
  alignItems: 'center',
  padding: '2px 4px',
  fontSize: '13px',
  lineHeight: '20px',
  gap: '1px',
}));

export const AWSEmulatorPill = (): JSX.Element => (
  <AWSPillWrapper>
    <EmulatorIcon emulator={EmulatorType.AWS} fontSize="small" />
    <b>{EMULATOR_LABELS[EmulatorType.AWS]}</b>
  </AWSPillWrapper>
);

const SnowflakePillWrapper = styled(AWSPillWrapper)(({ theme }) => ({
  background: theme.palette.snowflake.background,
  color: theme.palette.snowflake.text,
}));

export const SnowflakeEmulatorPill = (): JSX.Element => (
  <SnowflakePillWrapper>
    <EmulatorIcon emulator={EmulatorType.SNOWFLAKE} fontSize="small" />
    <b>{EMULATOR_LABELS[EmulatorType.SNOWFLAKE]}</b>
  </SnowflakePillWrapper>
);

const AzurePillWrapper = styled(AWSPillWrapper)(({ theme }) => ({
  background: theme.palette.azure.background,
  color: theme.palette.azure.text,
}));

export const AzureEmulatorPill = (): JSX.Element => (
  <AzurePillWrapper>
    <EmulatorIcon emulator={EmulatorType.AZURE} fontSize="small" />
    <b>{EMULATOR_LABELS[EmulatorType.AZURE]}</b>
  </AzurePillWrapper>
);

/**
 * Maps an emulator type to its pill, defaulting to the AWS pill for any
 * emulator without a dedicated pill.
 */
export const getEmulatorPill = (emulator: EmulatorType): JSX.Element => {
  switch (emulator) {
    case EmulatorType.SNOWFLAKE:
      return <SnowflakeEmulatorPill />;
    case EmulatorType.AZURE:
      return <AzureEmulatorPill />;
    default:
      return <AWSEmulatorPill />;
  }
};

interface EmulatorPillsProps {
  /** The emulators this subscription gives access to, decided by the caller. */
  emulators: EmulatorType[];
}

export const EmulatorPills = ({ emulators }: EmulatorPillsProps): JSX.Element => (
  <Stack direction="row" gap="4px">
    {emulators.map((emulator) => (
      <Fragment key={emulator}>{getEmulatorPill(emulator)}</Fragment>
    ))}
  </Stack>
);
